import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Save, Edit2, Trash2, X, Home, MessageSquare, Star, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

export default function HomeManager() {
  const [homeContent, setHomeContent] = useState({
    hero_title_en: '', hero_title_bg: '', hero_title_de: '',
    hero_subtitle_en: '', hero_subtitle_bg: '', hero_subtitle_de: '',
    hero_image: '/images/truck-hero.jpg',
    stats: [
      { number: '', label_en: '', label_bg: '', label_de: '' },
      { number: '', label_en: '', label_bg: '', label_de: '' },
      { number: '', label_en: '', label_bg: '', label_de: '' },
      { number: '', label_en: '', label_bg: '', label_de: '' }
    ],
    why_choose_title_en: '', why_choose_title_bg: '', why_choose_title_de: '',
    why_choose_subtitle_en: '', why_choose_subtitle_bg: '', why_choose_subtitle_de: '',
    why_choose_features_en: ['', '', '', '', ''],
    why_choose_features_bg: ['', '', '', '', ''],
    why_choose_features_de: ['', '', '', '', '']
  });
  const [testimonials, setTestimonials] = useState([]);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [editingTestimonialId, setEditingTestimonialId] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({ name: '', company: '', text_en: '', text_bg: '', text_de: '', rating: 5 });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeLang, setActiveLang] = useState('en');

  useEffect(() => {
    fetchHomeContent();
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/admin/testimonials`);
      setTestimonials(response.data.testimonials || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const handleSaveTestimonial = async () => {
    try {
      if (editingTestimonialId) {
        await axios.put(`${API}/admin/testimonials/${editingTestimonialId}`, testimonialForm);
        toast.success('Review updated!');
      } else {
        await axios.post(`${API}/admin/testimonials`, testimonialForm);
        toast.success('Review added!');
      }
      fetchTestimonials();
      setShowTestimonialForm(false);
      setEditingTestimonialId(null);
      setTestimonialForm({ name: '', company: '', text_en: '', text_bg: '', text_de: '', rating: 5 });
    } catch (error) {
      toast.error('Failed to save review');
    }
  };

  const handleEditTestimonial = (t) => {
    setTestimonialForm({ name: t.name, company: t.company, text_en: t.text_en, text_bg: t.text_bg, text_de: t.text_de || '', rating: t.rating });
    setEditingTestimonialId(t.id);
    setShowTestimonialForm(true);
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`);
      toast.success('Review deleted!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const fetchHomeContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/home-content`);
      if (response.data) {
        setHomeContent(prev => ({
          ...prev,
          ...response.data,
          stats: response.data.stats || prev.stats,
          why_choose_features_en: response.data.why_choose_features_en || prev.why_choose_features_en,
          why_choose_features_bg: response.data.why_choose_features_bg || prev.why_choose_features_bg,
          why_choose_features_de: response.data.why_choose_features_de || prev.why_choose_features_de
        }));
      }
    } catch (error) {
      console.error('Error fetching home content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/home-content`, homeContent);
      toast.success('Home page content saved!');
    } catch (error) {
      toast.error('Failed to save home content');
    } finally {
      setIsSaving(false);
    }
  };

  const updateStat = (index, field, value) => {
    const newStats = [...homeContent.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setHomeContent({ ...homeContent, stats: newStats });
  };

  const updateFeature = (lang, index, value) => {
    const field = `why_choose_features_${lang}`;
    const newFeatures = [...homeContent[field]];
    newFeatures[index] = value;
    setHomeContent({ ...homeContent, [field]: newFeatures });
  };

  const addFeature = () => {
    setHomeContent({
      ...homeContent,
      why_choose_features_en: [...homeContent.why_choose_features_en, ''],
      why_choose_features_bg: [...homeContent.why_choose_features_bg, ''],
      why_choose_features_de: [...homeContent.why_choose_features_de, '']
    });
  };

  const removeFeature = (index) => {
    setHomeContent({
      ...homeContent,
      why_choose_features_en: homeContent.why_choose_features_en.filter((_, i) => i !== index),
      why_choose_features_bg: homeContent.why_choose_features_bg.filter((_, i) => i !== index),
      why_choose_features_de: homeContent.why_choose_features_de.filter((_, i) => i !== index)
    });
  };

  const updateField = (field, value) => setHomeContent(prev => ({ ...prev, [field]: value }));

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Home className="w-5 h-5 text-[#C41E3A]" /> Hero Section
          </h3>
          
          <Tabs value={activeLang} onValueChange={setActiveLang}>
            <TabsList className="grid w-full grid-cols-3 mb-4">
              {LANGUAGES.map(lang => (
                <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                  {lang.flag} {lang.name}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {LANGUAGES.map(lang => (
              <TabsContent key={lang.code} value={lang.code}>
                <div className={`p-4 rounded-2xl space-y-4 ${lang.bg}`}>
                  <h4 className={`font-bold ${lang.text}`}>{lang.flag} Hero Content</h4>
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={homeContent[`hero_title_${lang.code}`] || ''}
                      onChange={(e) => updateField(`hero_title_${lang.code}`, e.target.value)}
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Textarea
                      value={homeContent[`hero_subtitle_${lang.code}`] || ''}
                      onChange={(e) => updateField(`hero_subtitle_${lang.code}`, e.target.value)}
                      className="mt-1 bg-white"
                      rows={2}
                    />
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Stats Section */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C41E3A]" /> Statistics
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {homeContent.stats.map((stat, index) => (
              <div key={index} className="p-4 border rounded-xl bg-slate-50">
                <Label className="text-sm font-medium text-slate-700">Stat #{index + 1}</Label>
                <div className="mt-2 space-y-2">
                  <Input
                    value={stat.number}
                    onChange={(e) => updateStat(index, 'number', e.target.value)}
                    placeholder="e.g., 20+"
                  />
                  <Tabs defaultValue="en" className="w-full">
                    <TabsList className="grid w-full grid-cols-3 h-8">
                      {LANGUAGES.map(lang => (
                        <TabsTrigger key={lang.code} value={lang.code} className="text-xs py-1">{lang.flag}</TabsTrigger>
                      ))}
                    </TabsList>
                    {LANGUAGES.map(lang => (
                      <TabsContent key={lang.code} value={lang.code} className="mt-2">
                        <Input
                          value={stat[`label_${lang.code}`] || ''}
                          onChange={(e) => updateStat(index, `label_${lang.code}`, e.target.value)}
                          placeholder={`Label (${lang.code.toUpperCase()})`}
                        />
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Why Choose Us Features */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-[#C41E3A]" /> Why Choose Us Section
          </h3>
          
          {/* Section Titles */}
          <Tabs defaultValue="en" className="w-full mb-6">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              {LANGUAGES.map(lang => (
                <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
              ))}
            </TabsList>
            {LANGUAGES.map(lang => (
              <TabsContent key={lang.code} value={lang.code}>
                <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={homeContent[`why_choose_title_${lang.code}`] || ''}
                      onChange={(e) => updateField(`why_choose_title_${lang.code}`, e.target.value)}
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input
                      value={homeContent[`why_choose_subtitle_${lang.code}`] || ''}
                      onChange={(e) => updateField(`why_choose_subtitle_${lang.code}`, e.target.value)}
                      className="mt-1 bg-white"
                    />
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>

          {/* Features */}
          <h4 className="font-medium text-slate-700 mb-3">Features / Checkmarks</h4>
          <div className="space-y-3">
            {homeContent.why_choose_features_en.map((_, index) => (
              <div key={index} className="p-3 border rounded-xl bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Feature #{index + 1}</Label>
                  <Button variant="ghost" size="sm" onClick={() => removeFeature(index)} className="text-red-500 h-6 w-6 p-0">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <Tabs defaultValue="en" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 h-8">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code} className="text-xs py-1">{lang.flag}</TabsTrigger>
                    ))}
                  </TabsList>
                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code} className="mt-2">
                      <Input
                        value={homeContent[`why_choose_features_${lang.code}`][index] || ''}
                        onChange={(e) => updateFeature(lang.code, index, e.target.value)}
                        placeholder={`Feature text (${lang.code.toUpperCase()})`}
                      />
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addFeature} className="mt-3">
            <Plus className="w-4 h-4 mr-1" /> Add Feature
          </Button>
        </CardContent>
      </Card>

      {/* Testimonials Section */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#C41E3A]" /> Customer Reviews
            </h3>
            <Button onClick={() => setShowTestimonialForm(!showTestimonialForm)} size="sm" className="bg-[#C41E3A] hover:bg-[#A01830]">
              <Plus className="w-4 h-4 mr-1" /> Add Review
            </Button>
          </div>

          {showTestimonialForm && (
            <div className="mb-6 p-4 border rounded-xl bg-slate-50">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>Name</Label>
                  <Input value={testimonialForm.name} onChange={(e) => setTestimonialForm({ ...testimonialForm, name: e.target.value })} placeholder="John Smith" />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input value={testimonialForm.company} onChange={(e) => setTestimonialForm({ ...testimonialForm, company: e.target.value })} placeholder="Company Name" />
                </div>
              </div>
              
              <Tabs defaultValue="en" className="w-full mb-4">
                <TabsList className="grid w-full grid-cols-3">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
                  ))}
                </TabsList>
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-3 rounded-xl ${lang.bg}`}>
                      <Label>Review Text ({lang.name})</Label>
                      <Textarea
                        value={testimonialForm[`text_${lang.code}`] || ''}
                        onChange={(e) => setTestimonialForm({ ...testimonialForm, [`text_${lang.code}`]: e.target.value })}
                        rows={2}
                        className="mt-1 bg-white"
                      />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <div className="mb-4">
                <Label>Rating</Label>
                <div className="flex gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button key={star} type="button" onClick={() => setTestimonialForm({ ...testimonialForm, rating: star })} className="p-1">
                      <Star className={`w-6 h-6 ${star <= testimonialForm.rating ? 'fill-[#C41E3A] text-[#C41E3A]' : 'text-slate-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveTestimonial} className="bg-[#C41E3A] hover:bg-[#A01830]">
                  <Save className="w-4 h-4 mr-1" /> {editingTestimonialId ? 'Update' : 'Add'}
                </Button>
                <Button variant="outline" onClick={() => { setShowTestimonialForm(false); setEditingTestimonialId(null); setTestimonialForm({ name: '', company: '', text_en: '', text_bg: '', text_de: '', rating: 5 }); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {testimonials.map((t) => (
              <div key={t.id} className="p-4 border rounded-xl bg-slate-50 flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex gap-1 mb-1">
                    {[...Array(t.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-[#C41E3A] text-[#C41E3A]" />
                    ))}
                  </div>
                  <div className="font-medium text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.company}</div>
                  <div className="flex gap-1 mt-2">
                    {LANGUAGES.map(lang => (
                      <span key={lang.code} className={`text-xs px-1.5 py-0.5 rounded ${t[`text_${lang.code}`] ? lang.bg : 'bg-slate-100 text-slate-400'}`}>{lang.flag}</span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEditTestimonial(t)}><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteTestimonial(t.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
            {testimonials.length === 0 && <p className="text-center text-slate-500 py-4">No reviews yet.</p>}
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={isSaving} className="w-full bg-[#C41E3A] hover:bg-[#A01830]">
        <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Home Page Content'}
      </Button>
    </div>
  );
}
